import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Eyebrow } from "@/components/editorial";
import { api, ApiError } from "@/lib/api";
import { formatTimer } from "@/hooks/useCookingTimer";
import type { RecipeBookEntry } from "@/types/recipeBook";
import { normalizeBookPayload } from "./normalizeBookPayload";

const recipeBookEntryKey = (id: string) => ["recipe-book", "entry", id];

/**
 * Detail view of one recipe-book entry (audit H13).
 *
 * Book entries are self-contained — favourites from past weeks, manual
 * recipes and restaurant captures are rendered from `entry.payload`, so
 * they open even when the recipe is not in the current week's plan.
 */
export function RecipeBookEntryPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const query = useQuery<RecipeBookEntry, Error>({
    queryKey: recipeBookEntryKey(id),
    queryFn: () =>
      api<RecipeBookEntry>(
        `/api/v1/favorites/recipe-book/${encodeURIComponent(id)}`,
      ),
    enabled: id.length > 0,
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const back = (
    <Link
      to="/recipe-book"
      aria-label={t("recipeBook.back")}
      className="flex h-9 w-9 items-center justify-center text-foreground"
    >
      <ChevronLeft className="size-5" />
    </Link>
  );

  if (query.isPending) {
    return (
      <div className="grid h-full place-items-center text-ink-50">
        {t("recipeBook.loading")}
      </div>
    );
  }

  if (query.isError || !query.data) {
    const notFound =
      query.error instanceof ApiError && query.error.status === 404;
    return (
      <div className="flex h-full flex-col bg-background">
        <header className="flex items-center gap-2 border-b border-hair px-3 py-3">
          {back}
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center">
          <p className="font-serif text-lg italic">
            {notFound ? t("recipeBook.not_found") : t("recipeBook.load_error")}
          </p>
          <Link to="/recipe-book" className="text-[13px] underline">
            {t("recipeBook.back")}
          </Link>
        </div>
      </div>
    );
  }

  const entry = query.data;
  const recipe = normalizeBookPayload(
    entry.payload,
    entry.recipe_id.replace(/-/g, " "),
  );
  const totalMinutes = (recipe.prepMinutes ?? 0) + (recipe.cookMinutes ?? 0);

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="flex items-center gap-2 border-b border-hair px-3 py-3">
        {back}
        <Eyebrow>{t(`recipeBook.source.${entry.source}`)}</Eyebrow>
      </header>

      <div className="flex-1 overflow-auto px-5 py-5">
        <h1 className="h-display text-[28px] leading-[1.05]">{recipe.title}</h1>
        {recipe.summary && (
          <p className="mt-3 text-[14px] leading-relaxed text-ink-50">
            {recipe.summary}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-4 text-[12px] uppercase tracking-[0.12em] text-ink-50">
          {recipe.servings !== null && (
            <span>{t("recipeBook.servings", { count: recipe.servings })}</span>
          )}
          {totalMinutes > 0 && (
            <span>{t("recipeBook.minutes", { count: totalMinutes })}</span>
          )}
        </div>

        <section className="mt-8">
          <h2 className="font-serif text-[18px]">{t("recipe.ingredients")}</h2>
          {recipe.ingredients.length === 0 ? (
            <p className="mt-2 text-[13px] text-ink-50">
              {t("recipeBook.no_ingredients")}
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-hair border-y border-hair">
              {recipe.ingredients.map((ing, i) => (
                <li
                  key={`${ing.name}-${i}`}
                  className="flex justify-between gap-4 py-2 text-[14px]"
                >
                  <span>{ing.name}</span>
                  {ing.quantity && (
                    <span className="font-mono text-[13px] text-ink-50">
                      {ing.quantity}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-8">
          <h2 className="font-serif text-[18px]">{t("recipe.steps")}</h2>
          {recipe.steps.length === 0 ? (
            <p className="mt-2 text-[13px] text-ink-50">
              {t("recipeBook.no_steps")}
            </p>
          ) : (
            <ol className="mt-3 space-y-4">
              {recipe.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-[14px] leading-snug">
                  <span className="font-mono text-[12px] text-ink-50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    {step.verb && (
                      <span className="mr-1.5 font-semibold tracking-[0.04em]">
                        {step.verb}
                      </span>
                    )}
                    <span>{step.text}</span>
                    {step.quantity && (
                      <span className="ml-1.5 text-ink-50">
                        ({step.quantity})
                      </span>
                    )}
                    {step.timerSeconds !== null && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-ink-50">
                        <Timer className="size-3" />
                        {formatTimer(step.timerSeconds)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
